import { isAxiosError } from "axios";
import { ServerError } from "utils/genericUtilityFunctions";

export type CommentAction = "post" | "reply" | "edit" | "delete" | "resolve" | "reopen";

const FALLBACK: Record<CommentAction, string> = {
  post: "Could not post the comment. Try again.",
  reply: "Could not post the reply. Try again.",
  edit: "Could not save your edit. Try again.",
  delete: "Could not delete the comment. Try again.",
  resolve: "Could not resolve the thread. Try again.",
  reopen: "Could not reopen the thread. Try again.",
};

/**
 * Toast text for a failed comment request, picked by HTTP status. Every comment mutation refetches
 * the list on 404, so the 404 copy can say the list was refreshed.
 */
export function commentErrorMessage(error: unknown, action: CommentAction): string {
  if (!isAxiosError(error)) return FALLBACK[action];
  if (!error.response) {
    return "Could not reach RivetAI. Check your connection and try again.";
  }

  const { status } = error.response;
  const serverError = ServerError.fromAxiosError(error);
  const message = serverError.message ?? "";

  if (status === 400) {
    if (/body must not be empty/i.test(message)) return "Write something before posting.";
    if (/body must be shorter/i.test(message)) return "Comments can be up to 4,000 characters.";
    return FALLBACK[action];
  }
  if (status === 403) {
    if (serverError.code === "SCRIPT_LOCKED") {
      return "The script is locked for production. Ask the project owner to unlock it to comment.";
    }
    if (/only edit your own/i.test(message)) return "You can only edit your own comments.";
    if (/only delete your own/i.test(message)) return "You can only delete your own comments.";
    return "You don't have permission to do that on this project.";
  }
  if (status === 404) {
    return action === "reply"
      ? "This thread was deleted. The list has been refreshed."
      : "This comment was deleted. The list has been refreshed.";
  }
  if (status === 429) return "Too many requests. Wait a moment and try again.";
  return FALLBACK[action];
}
